import { z } from "zod";
import {
  CustomerDocumentFileSchema,
  CustomerDocumentInputSchema,
  CustomerDocumentListSchema,
  CustomerDocumentResponseSchema,
  CustomerDocumentUpdateSchema,
  PublishedCustomerDocumentSchema,
  type CustomerDocumentInput,
  type CustomerDocumentKind,
} from "../../shared/customer-documents";
import { parseResponse } from "../api/client-core";

const root = (slug: string) => `/api/ops/customers/${encodeURIComponent(slug)}`;
export async function getCustomerDocumentOverview(slug: string, kind: CustomerDocumentKind) {
  const query = new URLSearchParams({
    kind,
    ...(kind === "agreement" ? { agreementType: "msa" } : {}),
  });
  return parseResponse(
    await fetch(`${root(slug)}/documents-overview?${query}`, { credentials: "same-origin" }),
    CustomerDocumentListSchema,
  );
}
const json = (method: string, body: unknown): RequestInit => ({
  method,
  credentials: "same-origin",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});
export async function listCustomerLibrary(
  slug: string,
  query: {
    kind?: CustomerDocumentKind;
    agreementType?: "msa";
    search?: string;
    cursor?: string;
    archived?: string;
  } = {},
) {
  return parseResponse(
    await fetch(`${root(slug)}/documents?${new URLSearchParams(query)}`, {
      credentials: "same-origin",
    }),
    CustomerDocumentListSchema,
  );
}
export async function getCustomerLibraryDocument(slug: string, id: string) {
  return parseResponse(
    await fetch(`${root(slug)}/documents/${encodeURIComponent(id)}`, {
      credentials: "same-origin",
    }),
    CustomerDocumentResponseSchema,
  );
}
export async function saveCustomerLibraryDocument(
  slug: string,
  input: CustomerDocumentInput,
  current?: { id: string; revision: number },
) {
  const url = `${root(slug)}/documents${current ? `/${encodeURIComponent(current.id)}` : ""}`;
  const body = current
    ? CustomerDocumentUpdateSchema.parse({ expectedRevision: current.revision, document: input })
    : CustomerDocumentInputSchema.parse(input);
  return parseResponse(
    await fetch(url, json(current ? "PUT" : "POST", body)),
    CustomerDocumentResponseSchema,
  );
}
export async function changeCustomerLibraryPublication(
  slug: string,
  document: { id: string; revision: number },
  action: "publish" | "unpublish" | "archive" | "restore",
) {
  return parseResponse(
    await fetch(
      `${root(slug)}/documents/${encodeURIComponent(document.id)}/${action}`,
      json("POST", { expectedRevision: document.revision }),
    ),
    CustomerDocumentResponseSchema,
  );
}
export async function uploadCustomerDocumentFile(
  slug: string,
  kind: CustomerDocumentKind,
  file: File,
) {
  return parseResponse(
    await fetch(`${root(slug)}/document-files?kind=${kind}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/pdf",
        "X-Document-Filename": encodeURIComponent(file.name),
      },
      body: file,
    }),
    CustomerDocumentFileSchema,
  );
}
export async function previewCustomerLibraryDocument(slug: string, id: string) {
  return parseResponse(
    await fetch(`${root(slug)}/document-preview/${encodeURIComponent(id)}`, {
      credentials: "same-origin",
    }),
    z.strictObject({ document: PublishedCustomerDocumentSchema }),
  );
}
